import React, { useState, useEffect } from 'react';
import { Clock, TrendingUp, Filter, Trash2, Edit3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BetHistory = () => {
  const { user } = useAuth();
  const [bets, setBets] = useState([]);
  const [filteredBets, setFilteredBets] = useState([]);
  const [bettingHouses, setBettingHouses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    result: 'all',
    house: 'all',
    group: 'all',
    period: 'all'
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBet, setEditingBet] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    odd: '',
    stake: '',
    result: 'green',
    cashout_value: '',
    betting_house_id: '',
    group_id: '',
    comments: ''
  });
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [filters, bets]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load bets
      const betsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets?user_id=${user.id}`);
      if (betsResponse.ok) {
        const betsData = await betsResponse.json();
        setBets(betsData);
      }

      // Load betting houses for filter
      const housesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses?user_id=${user.id}`);
      if (housesResponse.ok) {
        const housesData = await housesResponse.json();
        setBettingHouses(housesData);
      }

      // Load groups for filter
      const groupsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups?user_id=${user.id}`);
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData);
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...bets];

    // Filter by result
    if (filters.result !== 'all') {
      filtered = filtered.filter(bet => bet.result === filters.result);
    }

    // Filter by house
    if (filters.house !== 'all') {
      filtered = filtered.filter(bet => bet.betting_house_id === filters.house);
    }

    // Filter by group
    if (filters.group !== 'all') {
      filtered = filtered.filter(bet => bet.group_id === filters.group);
    }

    // Filter by period
    if (filters.period !== 'all') {
      const now = new Date();
      let startDate;

      switch (filters.period) {
        case '7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered.filter(bet => new Date(bet.date) >= startDate);
      }
    }

    setFilteredBets(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleDeleteBet = async (betId) => {
    if (!confirm('Tem certeza que deseja excluir esta aposta?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets/${betId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Aposta excluída com sucesso!');
        loadData();
      } else {
        throw new Error('Falha ao excluir');
      }
    } catch (error) {
      console.error('Error deleting bet:', error);
      alert('Erro ao excluir. Tente novamente.');
    }
  };

  const handleEditBet = (bet) => {
    setEditingBet(bet);
    setEditFormData({
      date: bet.created_at ? new Date(bet.created_at).toISOString().split('T')[0] : '',
      odd: bet.odd || '',
      stake: bet.stake || '',
      result: bet.result || 'green',
      cashout_value: bet.cashout_value || '',
      betting_house_id: bet.betting_house_id || '',
      group_id: bet.group_id || '',
      comments: bet.comments || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const updateData = {
        odd: parseFloat(editFormData.odd),
        stake: parseFloat(editFormData.stake),
        result: editFormData.result,
        betting_house_id: editFormData.betting_house_id,
        group_id: editFormData.group_id || null,
        comments: editFormData.comments || null
      };

      // Add cashout_value only if result is cashout
      if (editFormData.result === 'cashout' && editFormData.cashout_value) {
        updateData.cashout_value = parseFloat(editFormData.cashout_value);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets/${editingBet.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert('Aposta atualizada com sucesso!');
        setShowEditModal(false);
        setEditingBet(null);
        loadData();
      } else {
        throw new Error('Falha ao atualizar');
      }
    } catch (error) {
      alert('Erro ao atualizar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit'
    })}`;
  };

  const getResultColor = (result, status) => {
    if (status === 'pending') return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    switch (result) {
      case 'green': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'red': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'void': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      case 'cashout': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getResultLabel = (result, status) => {
    if (status === 'pending') return 'Pendente';
    switch (result) {
      case 'green': return 'Green';
      case 'red': return 'Red';
      case 'void': return 'Void';
      case 'cashout': return 'Cashout';
      default: return result;
    }
  };

  const getHouseName = (houseId) => {
    const house = bettingHouses.find(h => h.id === houseId);
    return house ? house.name : 'N/A';
  };

  const getGroupName = (groupId) => {
    if (!groupId) return 'Sem grupo';
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'N/A';
  };

  const calculateStats = () => {
    // Calcular apenas apostas finalizadas (não pendentes)
    const completedBets = filteredBets.filter(bet => bet.status !== 'pending');
    const totalBets = completedBets.length;
    const greenBets = completedBets.filter(bet => bet.result === 'green').length;
    const totalProfit = completedBets.reduce((sum, bet) => sum + (bet.profit || 0), 0);
    const totalVolume = completedBets.reduce((sum, bet) => sum + (bet.stake || 0), 0);
    const winRate = totalBets > 0 ? (greenBets / totalBets) * 100 : 0;

    return { totalBets, greenBets, totalProfit, totalVolume, winRate };
  };

  const stats = calculateStats();

  // Cálculos de paginação
  const totalPages = Math.ceil(filteredBets.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedBets = filteredBets.slice(startIndex, endIndex);

  // Resetar para página 1 quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Histórico de Apostas
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Acompanhe todas as suas apostas registradas
          </p>
        </div>
      </div>

      {/* Summary Cards - Mobile Optimized */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{stats.totalBets}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Taxa</p>
              <p className="text-lg sm:text-xl font-bold text-green-600 dark:text-green-400">{stats.winRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mb-2 sm:mb-0">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Volume</p>
              <p className="text-sm sm:text-lg font-bold text-purple-600 dark:text-purple-400">{formatCurrency(stats.totalVolume)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center mb-2 sm:mb-0 ${stats.totalProfit >= 0 ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-pink-600'}`}>
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Lucro</p>
              <p className={`text-sm sm:text-lg font-bold ${stats.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatCurrency(stats.totalProfit)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-3 sm:p-4 md:p-6">
        <div className="flex items-center space-x-2 mb-3 sm:mb-4">
          <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Filtros</h3>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Result Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Resultado
            </label>
            <select
              value={filters.result}
              onChange={(e) => handleFilterChange('result', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">Todos</option>
              <option value="green">Green</option>
              <option value="red">Red</option>
              <option value="void">Void</option>
              <option value="cashout">Cashout</option>
            </select>
          </div>

          {/* House Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Casa de Apostas
            </label>
            <select
              value={filters.house}
              onChange={(e) => handleFilterChange('house', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">Todas</option>
              {bettingHouses.map(house => (
                <option key={house.id} value={house.id}>{house.name}</option>
              ))}
            </select>
          </div>

          {/* Group Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Grupo
            </label>
            <select
              value={filters.group}
              onChange={(e) => handleFilterChange('group', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">Todos</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>

          {/* Period Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Período
            </label>
            <select
              value={filters.period}
              onChange={(e) => handleFilterChange('period', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">Todo o período</option>
              <option value="7days">Últimos 7 dias</option>
              <option value="30days">Últimos 30 dias</option>
              <option value="90days">Últimos 90 dias</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bets List - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            Apostas ({filteredBets.length})
          </h3>
        </div>
        <div className="p-2 sm:p-4 md:p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2 text-sm">Carregando...</p>
            </div>
          ) : filteredBets.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Clock className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
                Nenhuma aposta encontrada
              </h3>
              <p className="text-sm text-gray-500 px-4">
                {bets.length === 0 ? 'Vá para "Nova Aposta" para começar a registrar.' : 'Ajuste os filtros para ver outras apostas.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {paginatedBets.map(bet => (
                <div key={bet.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  {/* Mobile Layout */}
                  <div className="space-y-3">
                    {/* Header: Badge + Data */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getResultColor(bet.result, bet.status)}`}>
                          {getResultLabel(bet.result, bet.status)}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500">{formatDate(bet.date)}</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button
                          onClick={() => handleEditBet(bet)}
                          className="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Editar aposta"
                        >
                          <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBet(bet.id)}
                          className="p-1.5 sm:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Excluir aposta"
                        >
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Info: Casa e Grupo */}
                    <div className="flex items-center gap-2 flex-wrap text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">{getHouseName(bet.betting_house_id)}</span>
                      <span>•</span>
                      <span>{getGroupName(bet.group_id)}</span>
                    </div>

                    {/* Stats: Grid 2x2 em mobile, inline em desktop */}
                    <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-4">
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="text-xs text-gray-500">Odd:</span>
                        <span className="text-sm sm:ml-1 font-semibold text-gray-900 dark:text-white">{bet.odd}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="text-xs text-gray-500">Stake:</span>
                        <span className="text-sm sm:ml-1 font-semibold text-gray-900 dark:text-white">{formatCurrency(bet.stake)}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center col-span-2 sm:col-span-1">
                        <span className="text-xs text-gray-500">
                          {bet.status === 'pending' ? 'Status:' : 'Lucro:'}
                        </span>
                        <span className={`text-sm sm:ml-1 font-semibold ${
                          bet.status === 'pending' ? 'text-orange-600 dark:text-orange-400' :
                          bet.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {bet.status === 'pending' ? 'Aguardando' : formatCurrency(bet.profit)}
                        </span>
                      </div>
                    </div>

                    {/* Comments */}
                    {bet.comments && (
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-700">
                        {bet.comments}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Controles de Paginação */}
          {filteredBets.length > ITEMS_PER_PAGE && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Mostrando <span className="font-semibold">{startIndex + 1}</span> a{' '}
                <span className="font-semibold">{Math.min(endIndex, filteredBets.length)}</span> de{' '}
                <span className="font-semibold">{filteredBets.length}</span> apostas
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ← Anterior
                </button>

                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Mostrar apenas algumas páginas (primeira, última, atual e adjacentes)
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2 text-gray-500">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Próxima →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Edição */}
      {showEditModal && editingBet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Editar Aposta
              </h3>
            </div>
            
            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Casa de Apostas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Casa de Apostas *
                  </label>
                  <select
                    name="betting_house_id"
                    required
                    value={editFormData.betting_house_id}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Selecione uma casa de apostas</option>
                    {bettingHouses.map(house => (
                      <option key={house.id} value={house.id}>{house.name}</option>
                    ))}
                  </select>
                </div>

                {/* Grupo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Grupo (Opcional)
                  </label>
                  <select
                    name="group_id"
                    value={editFormData.group_id}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Selecione um grupo (opcional)</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                </div>

                {/* Odd */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Odd *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="odd"
                    required
                    value={editFormData.odd}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Stake */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Valor da Aposta (R$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="stake"
                    required
                    value={editFormData.stake}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Resultado */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Resultado *
                  </label>
                  <select
                    name="result"
                    required
                    value={editFormData.result}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="green">🟢 Green (Ganhou)</option>
                    <option value="red">🔴 Red (Perdeu)</option>
                    <option value="cashout">🔵 Cashout (Encerrada)</option>
                  </select>
                </div>

                {/* Cashout Value - Only show when result is cashout */}
                {editFormData.result === 'cashout' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valor do Cashout (R$) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="cashout_value"
                      required
                      value={editFormData.cashout_value}
                      onChange={handleEditInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Comentários */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Comentários (Opcional)
                  </label>
                  <textarea
                    name="comments"
                    rows={3}
                    value={editFormData.comments}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Adicione observações sobre esta aposta..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BetHistory;